// Iter36 — Content translation infrastructure (Level 2).
//
// Wraps any user-visible text node (incident title, description, comment, etc.)
// and translates it on display when the user's `display_locale` differs from
// 'original'. Originals are NEVER mutated. The badge is clickable to flip
// back to the original.
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { LOCALE_META, useT } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache keyed by `${sha-ish}:${target}`. Sha is approximated by
// the full source string itself — collisions are basically impossible at our
// scale and we have a server-side cache too.
const memo = new Map();

const fetchTranslation = async (text, target) => {
  const key = `${text}::${target}`;
  if (memo.has(key)) return memo.get(key);
  const p = axios
    .post(`${API}/translate`, { text, target_locale: target })
    .then((r) => r.data?.translated || r.data?.text || text)
    .catch(() => null);
  memo.set(key, p);
  return p;
};

/**
 * TranslatableText — show translated content when display_locale !== 'original',
 * with a small clickable badge that toggles back to the source.
 *
 * Props:
 *   text       — source string (always kept untouched)
 *   inline     — render inline (default) vs block
 *   skipBadge  — render translation without the badge
 *   sourceHint — optional 2-letter hint shown in the badge ("HU→EN")
 *   className  — passthrough
 */
export const TranslatableText = ({
  text,
  inline = true,
  skipBadge = false,
  sourceHint,
  className,
  style,
  testId,
}) => {
  const { displayLocale, t } = useT();
  const [translated, setTranslated] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const shouldTranslate = !!text && displayLocale && displayLocale !== "original";

  useEffect(() => {
    if (!shouldTranslate) {
      setTranslated(null);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchTranslation(text, displayLocale).then((val) => {
      if (cancelled || !mountedRef.current) return;
      setLoading(false);
      if (!val) {
        setFailed(true);
        setTranslated(null);
      } else {
        setTranslated(val);
      }
    });
    return () => { cancelled = true; };
  }, [text, displayLocale, shouldTranslate]);

  const Wrapper = inline ? "span" : "div";

  if (!text) return null;
  if (!shouldTranslate) {
    return (
      <Wrapper className={className} style={style} data-testid={testId}>
        {text}
      </Wrapper>
    );
  }

  if (loading && !translated) {
    return (
      <Wrapper className={className} style={style} data-testid={testId}>
        <span style={{ opacity: 0.6 }}>{text}</span>
        {!skipBadge && (
          <span
            className="ml-1.5 inline-block align-middle px-1.5 py-0.5 rounded"
            style={{
              background: "rgba(155,89,182,0.10)",
              border: "1px solid rgba(155,89,182,0.35)",
              color: "#c89adb",
              fontFamily: "DM Mono, monospace",
              fontSize: 9,
              letterSpacing: "0.6px",
            }}
            data-testid={testId ? `${testId}-loading` : undefined}
          >
            {t("translated_badge.translating")}
          </span>
        )}
      </Wrapper>
    );
  }

  if (failed) {
    return (
      <Wrapper className={className} style={style} data-testid={testId}>
        {text}
      </Wrapper>
    );
  }

  const show = showOriginal ? text : translated || text;
  const target = (LOCALE_META[displayLocale]?.short || displayLocale).toUpperCase();
  const fromShort = sourceHint ? sourceHint.toUpperCase() : null;
  const badgeLabel = `${t("translated_badge.label")} · ${fromShort ? `${fromShort}→${target}` : target}`;
  const badgeTitle = showOriginal
    ? t("translated_badge.show_translation")
    : t("translated_badge.show_original");

  return (
    <Wrapper className={className} style={style} data-testid={testId}>
      {show}
      {!skipBadge && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowOriginal((v) => !v);
          }}
          className="ml-1.5 inline-flex items-center align-middle px-1.5 py-0.5 rounded transition-colors"
          style={{
            background: showOriginal ? "rgba(155,89,182,0.04)" : "rgba(155,89,182,0.14)",
            border: `1px solid rgba(155,89,182,${showOriginal ? 0.30 : 0.55})`,
            color: "#c89adb",
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            letterSpacing: "0.6px",
            cursor: "pointer",
            lineHeight: 1.2,
          }}
          title={badgeTitle}
          aria-label={badgeTitle}
          data-testid={testId ? `${testId}-badge` : "translated-badge"}
          data-show-original={showOriginal ? "true" : "false"}
        >
          {showOriginal ? `${t("translated_badge.label")} (orig)` : badgeLabel}
        </button>
      )}
    </Wrapper>
  );
};
